import type { Action, StoreEnhancer, StoreCreator } from '@reduxjs/toolkit';

export const reduxBatch: StoreEnhancer = ((next: StoreCreator) => {
  let nextListeners = new Set<() => void>();
  let currentListeners = nextListeners;

  const ensureCanMutateNextListeners = () => {
    if (nextListeners === currentListeners) {
      nextListeners = new Set(nextListeners);
    }
  };

  const subscribe = (listener: () => void) => {
    let isSubscribed = true;
    ensureCanMutateNextListeners();
    nextListeners.add(listener);

    return () => {
      if (!isSubscribed) return;

      ensureCanMutateNextListeners();
      nextListeners.delete(listener);
      isSubscribed = false;
    };
  };

  const notifyListeners = () => {
    const listeners = Array.from(nextListeners);
    currentListeners = nextListeners;
    listeners.forEach((listener) => listener());
  };

  return (...args: Parameters<typeof next>) => {
    const store = next(...args);

    let receivedNotification = false;
    let inDispatch = false;

    const dispatchRecurse = (action: Action | Action[]): Action | Action[] =>
      Array.isArray(action) ? (action.map(dispatchRecurse) as Action[]) : store.dispatch(action);

    const dispatch = (action: Action | Action[]): Action | Action[] => {
      const reentrant = inDispatch;

      if (!reentrant) {
        receivedNotification = false;
        inDispatch = true;
      }

      const result = dispatchRecurse(action);
      const requiresNotification = receivedNotification && !reentrant;

      if (!reentrant) {
        receivedNotification = false;
        inDispatch = false;
      }

      if (requiresNotification) notifyListeners();

      return result;
    };

    store.subscribe(() => {
      if (inDispatch) {
        receivedNotification = true;
      } else {
        notifyListeners();
      }
    });

    return {
      ...store,
      dispatch,
      subscribe
    };
  };
}) as StoreEnhancer;
